import { checkboxClasses, createTheme } from '@mui/material';

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    common: {
      black: '#000000',
      white: '#FFFFFF',
    },
    primary: {
      main: '#F4F4F4',
      light: '#0E141C',
      contrastText: '#000814',
    },
    secondary: {
      main: '#000814',
      light: '#D5D5D5',
      contrastText: '#F4F4F4',
    },
    status: {
      main: '#999DA0',
    },
    background: {
      default: '#F4F4F4',
      paper: '#f0efea',
    },
    text: {
      primary: '#000814',
      secondary: '#00000061',
    },
    error: {
      main: '#D50000',
      dark: '#D50000',
    },
    success: {
      main: '#1da72b',
      dark: '#00EA80',
    },
    warning: {
      main: '#F3722C',
      contrastText: '#FFFFFF',
      dark: '#FFDA0B',
    },
    info: {
      main: '#2196F3',
      contrastText: '#FFFFFF',
    },
    grey: {
      50: 'rgba(0, 0, 0, 0.05)',
    },
  },
  components: {
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#000814',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#F4F4F4',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '& fieldset': {
            borderColor: '#000814',
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#000814',
            },
          },
          '& legend': { display: 'none' },
        },
        input: {
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px #F4F4F4 inset',
            WebkitTextFillColor: '#000814',
            borderRadius: 0,
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: 'primary.contrastText',
          borderRadius: '8px',
          textTransform: 'none',
          fontSize: '.875rem',
          borderColor: '#0E141C',
          fontWeight: '400',
          padding: '9.5px 10px',
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: '#000814',
            color: '#F4F4F4',
            borderColor: '#000814',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          whiteSpace: 'pre',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#C4C4C4',
          [`&.${checkboxClasses.checked}`]: {
            color: '#000814',
          },
        },
      },
    },
  },
});

// ECHARTS THEME -------------------------------------------------------------------------------------
// https://echarts.apache.org/en/theme-builder.html
// note: after using the theme-builder, click on download > json version, copy it, convert to js object
// https://www.convertsimple.com/convert-json-to-javascript/
// and set on the ECHARTS_LIGHT_THEME variable.
export const ECHARTS_LIGHT_THEME = {
  color: ['#3fb1e3', '#6be6c1', '#626c91', '#a0a7e6', '#c4ebad', '#96dee8'],
  backgroundColor: 'rgba(252,252,252,0)',
  textStyle: {},
  title: {
    textStyle: {
      color: '#666666',
    },
    subtextStyle: {
      color: '#999999',
    },
  },
  line: {
    itemStyle: {
      borderWidth: '2',
    },
    lineStyle: {
      width: '3',
    },
    symbolSize: '8',
    symbol: 'emptyCircle',
    smooth: false,
  },
  radar: {
    itemStyle: {
      borderWidth: '2',
    },
    lineStyle: {
      width: '3',
    },
    symbolSize: '8',
    symbol: 'emptyCircle',
    smooth: false,
  },
  bar: {
    itemStyle: {
      barBorderWidth: 0,
      barBorderColor: '#ccc',
    },
  },
  pie: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#ccc',
    },
  },
  scatter: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#ccc',
    },
  },
  boxplot: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#ccc',
    },
  },
  parallel: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#ccc',
    },
  },
  sankey: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#ccc',
    },
  },
  funnel: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#ccc',
    },
  },
  gauge: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#ccc',
    },
  },
  candlestick: {
    itemStyle: {
      color: '#e6a0d2',
      color0: 'transparent',
      borderColor: '#e6a0d2',
      borderColor0: '#3fb1e3',
      borderWidth: '2',
    },
  },
  graph: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#ccc',
    },
    lineStyle: {
      width: '1',
      color: '#cccccc',
    },
    symbolSize: '8',
    symbol: 'emptyCircle',
    smooth: false,
    color: ['#3fb1e3', '#6be6c1', '#626c91', '#a0a7e6', '#c4ebad', '#96dee8'],
    label: {
      color: '#ffffff',
    },
  },
  map: {
    itemStyle: {
      areaColor: '#eeeeee',
      borderColor: '#aaaaaa',
      borderWidth: 0.5,
    },
    label: {
      color: '#ffffff',
    },
    emphasis: {
      itemStyle: {
        areaColor: 'rgba(63,177,227,0.25)',
        borderColor: '#3fb1e3',
        borderWidth: 1,
      },
      label: {
        color: '#3fb1e3',
      },
    },
  },
  geo: {
    itemStyle: {
      areaColor: '#eeeeee',
      borderColor: '#aaaaaa',
      borderWidth: 0.5,
    },
    label: {
      color: '#ffffff',
    },
    emphasis: {
      itemStyle: {
        areaColor: 'rgba(63,177,227,0.25)',
        borderColor: '#3fb1e3',
        borderWidth: 1,
      },
      label: {
        color: '#3fb1e3',
      },
    },
  },
  categoryAxis: {
    axisLine: {
      show: true,
      lineStyle: {
        color: '#cccccc',
      },
    },
    axisTick: {
      show: false,
      lineStyle: {
        color: '#333',
      },
    },
    axisLabel: {
      show: true,
      color: '#999999',
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: ['#eeeeee'],
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(250,250,250,0.05)', 'rgba(200,200,200,0.02)'],
      },
    },
  },
  valueAxis: {
    axisLine: {
      show: true,
      lineStyle: {
        color: '#cccccc',
      },
    },
    axisTick: {
      show: false,
      lineStyle: {
        color: '#333',
      },
    },
    axisLabel: {
      show: true,
      color: '#999999',
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: ['#eeeeee'],
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(250,250,250,0.05)', 'rgba(200,200,200,0.02)'],
      },
    },
  },
  logAxis: {
    axisLine: {
      show: true,
      lineStyle: {
        color: '#cccccc',
      },
    },
    axisTick: {
      show: false,
      lineStyle: {
        color: '#333',
      },
    },
    axisLabel: {
      show: true,
      color: '#999999',
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: ['#eeeeee'],
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(250,250,250,0.05)', 'rgba(200,200,200,0.02)'],
      },
    },
  },
  timeAxis: {
    axisLine: {
      show: true,
      lineStyle: {
        color: '#cccccc',
      },
    },
    axisTick: {
      show: false,
      lineStyle: {
        color: '#333',
      },
    },
    axisLabel: {
      show: true,
      color: '#999999',
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: ['#eeeeee'],
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(250,250,250,0.05)', 'rgba(200,200,200,0.02)'],
      },
    },
  },
  toolbox: {
    iconStyle: {
      borderColor: '#999999',
    },
    emphasis: {
      iconStyle: {
        borderColor: '#666666',
      },
    },
  },
  legend: {
    textStyle: {
      color: '#999999',
    },
  },
  tooltip: {
    axisPointer: {
      lineStyle: {
        color: '#cccccc',
        width: 1,
      },
      crossStyle: {
        color: '#cccccc',
        width: 1,
      },
    },
  },
  timeline: {
    lineStyle: {
      color: '#626c91',
      width: 1,
    },
    itemStyle: {
      color: '#626c91',
      borderWidth: 1,
    },
    controlStyle: {
      color: '#626c91',
      borderColor: '#626c91',
      borderWidth: 0.5,
    },
    checkpointStyle: {
      color: '#3fb1e3',
      borderColor: '#3fb1e3',
    },
    label: {
      color: '#626c91',
    },
    emphasis: {
      itemStyle: {
        color: '#626c91',
      },
      controlStyle: {
        color: '#626c91',
        borderColor: '#626c91',
        borderWidth: 0.5,
      },
      label: {
        color: '#626c91',
      },
    },
  },
  visualMap: {
    color: ['#2a99c9', '#afe8ff'],
  },
  dataZoom: {
    backgroundColor: 'rgba(255,255,255,0)',
    dataBackgroundColor: 'rgba(222,222,222,1)',
    fillerColor: 'rgba(114,230,212,0.25)',
    handleColor: '#cccccc',
    handleSize: '100%',
    textStyle: {
      color: '#999999',
    },
  },
  markPoint: {
    label: {
      color: '#ffffff',
    },
    emphasis: {
      label: {
        color: '#ffffff',
      },
    },
  },
};
// END ECHARTS THEME ---------------------------------------------------------------------------------
